import { useCallback, useRef, useEffect } from 'react';
import useProduct from 'hooks/useProduct';
import usePestsDataV2 from 'hooks/usePestsDataV2';
import {
  Product,
  ProductQuantityType,
  ApplicationMethod
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { PestOption } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import getEnumKeyByValue from 'helpers/getEnumKeyByValue';
import { getRegistrationNumber } from 'screens/ProductList/helpers/filteredProducts';

interface UseMapProductsReturn {
  getUniqueProducts: (products: Product[]) => Product[];
  fetchProductData: (
    product: Product
  ) => Promise<{
    productId: string;
    data: any;
  } | null>;
  buildProductsDataMap: (products: Product[]) => Promise<Map<string, any>>;
  fetchAllPests: (products: Product[]) => Promise<PestOption[]>;
  mapProducts: (
    products: Product[],
    productsFullDataMap: Map<string, any>,
    allPests: PestOption[],
    totalAppliedArea: number
  ) => any[];
}

const useMapProducts = (): UseMapProductsReturn => {
  const { fetchProductData } = useProduct();
  const { getPestsByNameIds } = usePestsDataV2('', { autoSearch: false });

  const getPestsByNameIdsRef = useRef(getPestsByNameIds);
  useEffect(() => {
    getPestsByNameIdsRef.current = getPestsByNameIds;
  }, [getPestsByNameIds]);

  const stableGetPestsByNameIds = useCallback(async (nameIds: string[]) => {
    return getPestsByNameIdsRef.current(nameIds);
  }, []);

  const getUniqueProducts = useCallback((products: Product[]) => {
    return products.filter(
      (product, index, self) =>
        self.findIndex(p => p.productId === product.productId) === index
    );
  }, []);

  const buildProductsDataMap = useCallback(
    async (products: Product[]) => {
      const uniqueProducts = getUniqueProducts(products);
      const productDataPromises = uniqueProducts.map(product =>
        fetchProductData(product)
      );
      const productDataResults = await Promise.all(productDataPromises);

      return new Map(
        productDataResults
          .filter(
            (result): result is NonNullable<typeof result> => result !== null
          )
          .map(result => [result.productId, result.data])
      );
    },
    [getUniqueProducts, fetchProductData]
  );

  const fetchAllPests = useCallback(
    async (products: Product[]): Promise<PestOption[]> => {
      const targetPestNameIds = products.flatMap(
        product => product.targetPestNameIds || []
      );

      return targetPestNameIds.length > 0
        ? stableGetPestsByNameIds(targetPestNameIds)
        : [];
    },
    [stableGetPestsByNameIds]
  );

  const mapProducts = useCallback(
    (
      products: Product[],
      productsFullDataMap: Map<string, any>,
      allPests: PestOption[],
      totalAppliedArea: number
    ) =>
      products.map(product => {
        const fullData = productsFullDataMap.get(product.productId);
        return {
          id: product.productId,
          productApplicationId: globalThis.crypto.randomUUID(),
          name: fullData?.name || '',
          manufacturer: fullData?.manufacturer,
          productType: fullData?.productType,
          quantityMode: getEnumKeyByValue(
            ProductQuantityType,
            product.productQuantityType
          ),
          ratePerAcre: product.ratePerAreaValue,
          ratePerAreaUnit: product.ratePerAreaUnit,
          totalProduct: product.totalProductValue,
          totalProductUnit: product.totalProductUnit,
          ratePerTank: product.ratePerTankValue,
          ratePerTankUnit: product.ratePerTankUnit,
          priceValue: product.priceValue,
          priceUnit: product.priceUnit,
          totalCost: product.totalCostValue || 0,
          appliedAreaValue: product.appliedAreaValue,
          coveragePercent: product.appliedAreaCoveragePercent,
          applicationMethod: getEnumKeyByValue(
            ApplicationMethod,
            product.applicationMethod
          ),
          targetPests: allPests.filter(pest =>
            product.targetPestNameIds?.includes(pest?.nameId)
          ),
          registrationNumber: getRegistrationNumber(fullData),
          custom: product.custom,
          isCarrier: product.isCarrier,
          isHidden: product.isHidden,
          isInUse: true,
          totalAppliedAcreage: totalAppliedArea,
          stdfactor: fullData?.stdfactor,
          stdunit: fullData?.stdunit,
          stdpackageunit: fullData?.stdpackageunit,
          density: fullData?.density
        };
      }),
    []
  );

  return {
    getUniqueProducts,
    fetchProductData,
    buildProductsDataMap,
    fetchAllPests,
    mapProducts
  };
};

export default useMapProducts;
